import React from "react";
import {Chip, Grid, Tooltip, Typography} from "@mui/material";
import { styled } from '@mui/material/styles';
import moment from "moment";
import {rtrim, SyncStatusColorMapper} from "../../../../../utils/utils";
import CatalogCheckbox from "../../../../../components/EnhancedCheckbox";

interface TaxonomyRow {
    Path?: string;
    Name?: string;
    SyncStatus?: string;
    Description?: string;
    Options?: string[];
    LastModified?: string | number | Date;
    Attributes?: string[];
    System?: boolean;
    IncludedInSet?: boolean;
    [key: string]: any;
}

const WrapText = styled(Typography)({
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: "2",
    WebkitBoxOrient: "vertical",
});

interface PathColumnProps {
    row: TaxonomyRow;
    column?: any;
    colIndex?: number;
    rowIndex?: number;
    addFilter?: (filter: { field: string; operator: string; value: any }) => void;
}

export const PathColumn = (props: PathColumnProps) => {
    const {row} = props;

    return (
        <WrapText>
            {rtrim(row.Path || "", row.Name)} <b>{row.Name}</b>
        </WrapText>
    )
}

interface SyncStatusColumnProps {
    row: TaxonomyRow;
    addFilter: (filter: { field: string; operator: string; value: any }) => void;
    column?: any;
    colIndex?: number;
    rowIndex?: number;
}

export const SyncStatusColumn = (props: SyncStatusColumnProps) => {
    const {row, addFilter} = props;
    if(row.SyncStatus === "" || row.SyncStatus == null) return (<></>);
    return (
        <Chip label={row.SyncStatus} size={"small"} onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            addFilter({
                field: "SyncStatus",
                operator: "eq",
                value: row.SyncStatus
            })
        }}
              style={{
                  color: "white",
                  backgroundColor: SyncStatusColorMapper(row.SyncStatus)
              }}/>
    )
}

interface DescriptionColumnProps {
    row: TaxonomyRow;
    column?: any;
    colIndex?: number;
    rowIndex?: number;
    addFilter?: (filter: { field: string; operator: string; value: any }) => void;
}

export const DescriptionColumn = (props: DescriptionColumnProps) => {
    const {row} = props;
    return (
        <WrapText>
            {row.Description}
        </WrapText>
    )
}

interface OptionsColumnProps {
    row: TaxonomyRow;
    column?: any;
    colIndex?: number;
    rowIndex?: number;
    addFilter?: (filter: { field: string; operator: string; value: any }) => void;
}

export const OptionsColumn = (props: OptionsColumnProps) => {
    const {row} = props;
    return (
        <>
            {row.Options == null ? <></> : row.Options.map((option, index) => {
                return (
                    <Chip key={`${option}-${index}`} size={"small"}
                          variant="outlined" style={{margin: "2px"}}
                          label={option}/>
                )
            })}
        </>
    )
}

interface LastModifiedDateProps {
    row: TaxonomyRow;
    column?: any;
    colIndex?: number;
    rowIndex?: number;
    addFilter?: (filter: { field: string; operator: string; value: any }) => void;
}

export const LastModifiedDate = (props: LastModifiedDateProps) => {
    const {row} = props;
    return (
        <>
            {row.LastModified !== undefined && (
                <Typography>{moment(row.LastModified).fromNow()}</Typography>
            )}
        </>
    )
}

interface AttributesColumnProps {
    row: TaxonomyRow;
    column?: any;
    colIndex?: number;
    rowIndex?: number;
    addFilter?: (filter: { field: string; operator: string; value: any }) => void;
}

export const AttributesColumn = (props: AttributesColumnProps) => {
    const {row} = props;
    return (
        <>
            {row.Attributes == null ? <></> : row.Attributes.map((attribute, index) => {
                if (index > 6) return null;
                if (index === 6) {
                    return (
                        <Tooltip title={row.Attributes!.reduce(
                            (accumulator: string, currentValue: string, currentIndex: number) => {
                                if (currentIndex > 6) {
                                    return accumulator + "\n" + currentValue;
                                }
                                return accumulator;
                            }
                        )}>
                            <Chip key={`${attribute}-${index}`}
                                  size={"small"}
                                  variant="outlined"
                                  label={"..."}/>
                        </Tooltip>
                    )
                }
                return (
                    <Chip key={`${attribute}-${index}`} size={"small"}
                          variant="outlined" style={{margin: "2px"}}
                          label={attribute}/>
                )
            })}
        </>
    )
}

interface IncludedInSetColumnProps {
    row: TaxonomyRow;
    attributeSet: { Attributes: string[]; [key: string]: any };
    setAttributeSet: (attributeSet: any) => void;
    hasPermission: boolean;
}

export const IncludedInSetColumn = (props: IncludedInSetColumnProps) => {
    const {row, attributeSet, setAttributeSet, hasPermission} = props;

    return (
        <Grid container justifyContent={"center"}>
            <CatalogCheckbox
                disabled={row.System || !hasPermission}
                checked={row.IncludedInSet}
                onChange={(e: any) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (row.IncludedInSet) {
                        setAttributeSet({
                            ...attributeSet,
                            Attributes: attributeSet.Attributes.filter((attribute: string) => attribute !== row.Name)
                        })
                    } else {
                        setAttributeSet({
                            ...attributeSet,
                            Attributes: [
                                ...attributeSet.Attributes,
                                row.Name
                            ]
                        })
                    }
                }}
            />
        </Grid>
    );
}

